import {
  computed, Injectable, signal, Signal,
} from '@angular/core';


import { TranslocoService } from '@jsverse/transloco';
import { IL10nsStrings, TimeagoIntl } from 'ngx-timeago';
import { strings as stringsEN } from 'ngx-timeago/language-strings/en';
import { strings as stringsFR } from 'ngx-timeago/language-strings/fr';
import { strings as stringsNL } from 'ngx-timeago/language-strings/nl';
import { distinctUntilChanged, ReplaySubject } from 'rxjs';

import {
  filterLanguagesByCountry, getOfficeLanguage, Language, resolveLanguageForCountry,
} from '../models/language.model';


@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly language$ = new ReplaySubject<Language>(1);

  readonly languages: Signal<Language[]> = computed(
    () => filterLanguagesByCountry(this.officeCountry(), this.getAvailableLanguages())
  );

  private readonly officeCountry = signal<string | null>(null);

  private officeLanguagePending = false;

  private readonly strings: Record<Language, IL10nsStrings> = {
    nl: stringsNL,
    'be-nl': stringsNL,
    en: stringsEN,
    fr: stringsFR,
  };

  constructor(
    private timeago: TimeagoIntl,
    private translocoService: TranslocoService
  ) {
    this.translocoService.langChanges$
      .pipe(
        distinctUntilChanged()
      )
      .subscribe((lang) => {
        const language = lang as Language;
        this.language$.next(language);
        this.applyTimeagoLanguage(language);
      });
  }

  changeLanguage(language: Language): void {
    this.translocoService.setActiveLang(language);
  }

  getAvailableLanguages(): Language[] {
    const availableLangs = this.translocoService.getAvailableLangs();

    return availableLangs.map(
      lang => (typeof lang === 'string' ? lang : lang.id) as Language
    );
  }

  /**
   * Switches to the office's language as soon as the office country is known, overriding the
   * persisted choice. Called on login; a language picked afterwards is kept as usual.
   */
  resetToOfficeLanguage(): void {
    this.officeLanguagePending = true;
  }

  setOfficeCountry(countryCode: string | null | undefined): void {
    this.officeCountry.set(countryCode ?? null);

    const officeLanguage = this.officeLanguagePending ? getOfficeLanguage(countryCode) : null;

    if (officeLanguage) {
      this.officeLanguagePending = false;
    }

    const activeLanguage = this.translocoService.getActiveLang() as Language;
    const resolved = resolveLanguageForCountry(officeLanguage ?? activeLanguage, this.languages());

    if (resolved !== activeLanguage) {
      this.changeLanguage(resolved);
    }
  }

  private applyTimeagoLanguage(language: Language): void {
    this.timeago.strings = this.strings[language];
    this.timeago.changes.next();
  }
}
